import { useEffect, useCallback, useMemo } from 'react';
import {
  createProcessSession,
  validateProcessSession,
  cleanupExpiredSessions,
  getSessionTimestamp
} from '@/src/shared/utils/encryption';

interface ProcessSecurityState {
  isValid: boolean;
  error: string | null;
  sessionAge: number | null;
}

export function useProcessSecurity(processInstanceId: string | null) {
  const storageKey = useMemo(() => 
    processInstanceId ? `process_session_${processInstanceId}` : null, 
    [processInstanceId]
  );

  const createSession = useCallback((targetProcessId?: string) => {
    const targetId = targetProcessId || processInstanceId;
    const targetKey = targetProcessId ? `process_session_${targetProcessId}` : storageKey;
    
    if (!targetId || !targetKey) return false;

    try {
      localStorage.setItem(targetKey, createProcessSession(targetId));
      return true;
    } catch (error) {
      return false;
    }
  }, [processInstanceId, storageKey]);

  const validateSession = useCallback((): ProcessSecurityState => {
    if (!processInstanceId || !storageKey) {
      return { isValid: false, error: 'Missing process instance ID', sessionAge: null };
    }

    try {
      const encryptedSession = localStorage.getItem(storageKey);
      if (!encryptedSession) {
        return { isValid: false, error: 'Session not found in this browser', sessionAge: null };
      }

      const isValid = validateProcessSession(encryptedSession, processInstanceId);
      if (!isValid) {
        localStorage.removeItem(storageKey);
        return { isValid: false, error: 'Session expired or invalid', sessionAge: null };
      }

      const timestamp = getSessionTimestamp(encryptedSession);
      const sessionAge = timestamp ? Math.floor((Date.now() - timestamp) / (1000 * 60 * 60 * 24)) : null;

      return { isValid: true, error: null, sessionAge };
    } catch (error) {
      return { isValid: false, error: 'Session validation failed', sessionAge: null };
    }
  }, [processInstanceId, storageKey]);

  const removeSession = useCallback(() => {
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  }, [storageKey, processInstanceId]);

  useEffect(() => {
    cleanupExpiredSessions();
  }, []);

  return {
    createSession,
    validateSession,
    removeSession,
    hasActiveSession: () => validateSession().isValid,
    storageKey
  };
}

export function useProcessSecurityGuard(
  processInstanceId: string | null,
  onSecurityViolation?: () => void
) {
  const { validateSession, createSession } = useProcessSecurity(processInstanceId);

  useEffect(() => {
    if (!processInstanceId) return;

    const securityState = validateSession();
    
    if (!securityState.isValid) {
      onSecurityViolation?.();
    }
  }, [processInstanceId, validateSession, onSecurityViolation]);

  return { validateSession, createSession };
}